// src/pages/SalesPages/PaymentModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import styles from "./PaymentModal.module.css";
import ReceiptModal from "./ReceiptModal";
import ClientSelectModal from "./ClientSelectModal";
import { API_URL } from "../../config/api";

const PaymentModal = ({ open, onClose, total, warehouse, products, onSuccess }) => {
  const [activeTab, setActiveTab] = useState("card");
  const DEBT_WALLET = { wallet_id: null, title: "В долг" };
  const [payments, setPayments] = useState([]);
  const [activePaymentIndex, setActivePaymentIndex] = useState(0);
  const [showClientSelect, setShowClientSelect] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [amountInput, setAmountInput] = useState("0");

  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const [shiftId, setShiftId] = useState(null);
  const [shiftNum, setShiftNum] = useState(null); // ✅ shift_num для чека
  const [wallets, setWallets] = useState([]);
  const [cashWalletId, setCashWalletId] = useState(null);

  const [storageAddress, setStorageAddress] = useState(null); // ✅ адрес склада для чека

  const [showWalletMenu, setShowWalletMenu] = useState(false);
  const [loading, setLoading] = useState(false);

  // ====== DISCOUNT (скидка на чек) ======
  const [showDiscount, setShowDiscount] = useState(false);
  const [discountType, setDiscountType] = useState("percent"); // "percent" | "amount"
  const [discountInput, setDiscountInput] = useState("0"); // строка

  const baseTotal = Number(total ?? 0);

  const discountAmount = useMemo(() => {
    const v = discountInput === "" || discountInput === "." ? 0 : Number(discountInput);
    const value = Number.isFinite(v) ? v : 0;

    if (discountType === "percent") {
      const pct = Math.max(0, Math.min(100, value));
      return (baseTotal * pct) / 100;
    }

    // amount
    return Math.max(0, Math.min(baseTotal, value));
  }, [discountInput, discountType, baseTotal]);

  const finalTotal = useMemo(() => {
    const t = baseTotal - discountAmount;
    return t > 0 ? t : 0;
  }, [baseTotal, discountAmount]);

  // ===== helpers =====
  const normalizeNumber = (val) => {
    if (val === "" || val === ".") return 0;
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };

  const ensureOnePaymentOnOpen = () => {
    const firstCardWallet = wallets?.[0];
    const initAmountStr = String(baseTotal ?? 0);

    const init = {
      type: "card",
      amount: normalizeNumber(initAmountStr),
      amountStr: initAmountStr,
      wallet_id: firstCardWallet?.wallet_id ?? null,
      title: firstCardWallet?.title ?? "Карта",
    };

    setActiveTab("card");
    setPayments([init]);
    setActivePaymentIndex(0);
    setAmountInput(init.amountStr);

    // сброс выбранного клиента при открытии
    setSelectedClient(null);
    setShowClientSelect(false);
  };

  // ===== on open init =====
  useEffect(() => {
    if (!open) return;

    // при открытии сбрасываем скидку
    setShowDiscount(false);
    setDiscountType("percent");
    setDiscountInput("0");

    ensureOnePaymentOnOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, total, wallets]);

  // ===== shift =====
  useEffect(() => {
    if (!open || !warehouse?.id) return;

    const fetchActiveShift = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const res = await fetch(
          `${API_URL}/api/shifts?status=active&storage_id=${warehouse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          console.error("❌ Server returned non-JSON:", text?.slice(0, 200));
          throw new Error("Ответ сервера не JSON (проверь API_URL / токен / прокси)");
        }

        const activeShift = data?.data?.[0];
        if (!activeShift) {
          alert("Активная смена не найдена. Откройте смену.");
          return;
        }

        setShiftId(activeShift.id);
        setShiftNum(activeShift.shift_num ?? null); // ✅
      } catch (err) {
        console.error("🔥 Ошибка получения смены:", err);
      }
    };

    fetchActiveShift();
  }, [open, warehouse]);

  // ===== storage info (address) =====
  useEffect(() => {
    if (!open || !warehouse?.id) return;

    const fetchStorage = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const res = await fetch(`${API_URL}/api/storages/${warehouse.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          console.error("❌ storages/:id non-JSON:", text?.slice(0, 200));
          return;
        }

        setStorageAddress(data?.data?.address ?? null); // ✅
      } catch (err) {
        console.error("🔥 Ошибка получения склада:", err);
      }
    };

    fetchStorage();
  }, [open, warehouse]);

  // ===== wallets =====
  useEffect(() => {
    const fetchWallets = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/api/payment_methods`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          console.error("❌ payment_methods non-JSON:", text?.slice(0, 200));
          return;
        }

        setWallets(data?.data || []);
      } catch (error) {
        console.error("Ошибка загрузки wallets", error);
      }
    };
    fetchWallets();
  }, []);

  // ===== cash wallet id =====
  useEffect(() => {
    if (!open || !warehouse?.id) return;

    const fetchCashWallet = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${API_URL}/api/wallets?type=cash&storage_id=${warehouse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          console.error("❌ cash wallets non-JSON:", text?.slice(0, 200));
          return;
        }

        const walletId = data?.data?.[0]?.id ?? null;
        setCashWalletId(walletId);
      } catch (err) {
        console.error("Ошибка при загрузке наличных кошельков", err);
      }
    };

    fetchCashWallet();
  }, [open, warehouse]);

  // ===== derived =====
  const totalPaid = useMemo(
    () => payments.reduce((sum, p) => sum + Number(p.amount || 0), 0),
    [payments]
  );

  const change = totalPaid - Number(finalTotal || 0);

  // ✅ ESLint fix: добавили payments в зависимости
  useEffect(() => {
    const p = payments[activePaymentIndex];
    if (!p) return;
    setAmountInput(p.amountStr ?? String(p.amount ?? 0));
  }, [activePaymentIndex, payments]);

  const setPaymentAmountByInput = (nextInput) => {
    setAmountInput(nextInput);

    const nextNum = normalizeNumber(nextInput);

    setPayments((prev) =>
      prev.map((p, idx) =>
        idx === activePaymentIndex
          ? { ...p, amountStr: nextInput, amount: nextNum }
          : p
      )
    );
  };

  // ===== keypad =====
  const handleKeyClick = (digit) => {
    let next = amountInput;

    if (next === "0" && digit !== ".") next = "";
    if (digit === "." && next.includes(".")) return;

    next = next + digit;
    setPaymentAmountByInput(next);
  };

  const handleDelete = () => {
    let next = amountInput;

    if (!next || next.length === 0) {
      setPaymentAmountByInput("");
      return;
    }
    if (next.length === 1) {
      setPaymentAmountByInput("");
      return;
    }
    next = next.slice(0, -1);
    setPaymentAmountByInput(next);
  };

  // ===== tabs / type changes =====
  const applyTypeToActivePayment = async (type) => {
    setActiveTab(type);

    setPayments((prev) => {
      const copy = [...prev];
      if (!copy[activePaymentIndex]) return prev;

      if (type === "cash") {
        copy[activePaymentIndex] = {
          ...copy[activePaymentIndex],
          type: "cash",
          title: undefined,
          wallet_id: cashWalletId ?? copy[activePaymentIndex].wallet_id,
        };
      } else {
        const w0 = wallets?.[0];
        copy[activePaymentIndex] = {
          ...copy[activePaymentIndex],
          type: "card",
          wallet_id: copy[activePaymentIndex].wallet_id ?? w0?.wallet_id,
          title: copy[activePaymentIndex].title ?? w0?.title,
        };
      }
      return copy;
    });
  };

  const handleSelectWallet = (wallet) => {
    if (wallet?.wallet_id === null && wallet?.title === "В долг") {
      setPayments((prev) =>
        prev.map((p, idx) =>
          idx === activePaymentIndex && p.type === "card"
            ? { ...p, wallet_id: null, title: "В долг" }
            : p
        )
      );

      setShowWalletMenu(false);
      setShowClientSelect(true);
      return;
    }

    // обычный кошелек
    setPayments((prev) =>
      prev.map((p, idx) =>
        idx === activePaymentIndex && p.type === "card"
          ? { ...p, wallet_id: wallet.wallet_id, title: wallet.title }
          : p
      )
    );

    setShowWalletMenu(false);
  };

  // ===== add / remove =====
  const addPayment = () => {
    const w0 = wallets?.[0];
    const newPay =
      activeTab === "cash"
        ? { type: "cash", amount: 0, amountStr: "0", wallet_id: cashWalletId ?? null }
        : { type: "card", amount: 0, amountStr: "0", wallet_id: w0?.wallet_id, title: w0?.title };

    setPayments((prev) => {
      const next = [...prev, newPay];
      const idx = next.length - 1;
      setActivePaymentIndex(idx);
      setAmountInput(newPay.amountStr);
      return next;
    });
  };

  const removePayment = (index) => {
    setPayments((prev) => {
      const next = prev.filter((_, i) => i !== index);
      const newActive = Math.max(0, Math.min(activePaymentIndex, next.length - 1));
      setActivePaymentIndex(newActive);
      return next.length ? next : prev;
    });
  };

  // ===== submit =====
  const handleSubmit = async () => {
    if (!products || products.length === 0) return;

    if (totalPaid < finalTotal) {
      alert("Сумма оплаты меньше суммы товаров!");
      return;
    }

    // ✅ если есть оплата "в долг" — клиент обязателен
    const hasDebt = payments.some((p) => p.type === "card" && p.wallet_id === null);
    if (hasDebt && !selectedClient?.id) {
      alert("Выберите клиента для оплаты в долг");
      setShowClientSelect(true);
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      alert("Нет токена! Авторизация не выполнена.");
      return;
    }

    const items = (Array.isArray(products) ? products : []).map((p) => ({
      product_id: p.product_id ?? p.id,
      quantity: Number(p.qty ?? p.quantity ?? p.count ?? p.amount ?? 1),
      price: Number(p.price ?? 0),
    }));

    const body = {
      storage_id: warehouse?.id,
      shift_id: shiftId,
      status: "paid",
      sum: Number(finalTotal ?? 0),
      client_id: selectedClient?.id ?? null,
      discount: {
        type: discountType,
        value: normalizeNumber(discountInput),
        amount: Number(discountAmount ?? 0),
      },
      items,
      payments: payments.map((p) => ({
        wallet_id: p.wallet_id,
        amount: Number(p.amount ?? 0),
      })),
    };

    try {
      setLoading(true);

      const accRes = await fetch(`${API_URL}/api/account`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const accText = await accRes.text();
      const accData = JSON.parse(accText);

      const res = await fetch(`${API_URL}/api/sales`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        console.error("❌ sales non-JSON:", text?.slice(0, 200));
        throw new Error("Ответ /api/sales не JSON");
      }

      if (res.ok) {
        if (onSuccess) onSuccess();

        const receipt = {
          companyName: accData?.data?.org?.name || "Неизвестная организация",
          bin: accData?.data?.org?.sn || "------",
          checkNumber: data?.data?.sale_id || Math.floor(Math.random() * 1000000000),
          employee: accData?.data?.name || "Сотрудник",

          // ✅ shift_num
          shiftNumber: shiftNum ?? "—",

          items: (Array.isArray(products) ? products : []).map((p) => {
            const qty = Number(p.qty ?? p.quantity ?? p.count ?? 1);
            const price = Number(p.price ?? 0);
            return { name: p.name || "Товар", price, qty, total: price * qty };
          }),
          originalTotal: baseTotal,
          discount: {
            type: discountType,
            value: normalizeNumber(discountInput),
            amount: discountAmount,
          },
          total: Number(finalTotal ?? 0),
          paymentType: payments
            .map((p) => (p.type === "cash" ? "Наличные" : p.title || "Карта"))
            .join(", "),
          vat: 0,

          // ✅ address из /api/storages/:id
          address: storageAddress || warehouse?.address || "---------",

          time: new Date().toLocaleString(),
          qrCode: data?.data?.qr_code || "",
          paid: totalPaid,
          change: change > 0 ? change : 0,
        };

        setReceiptData(receipt);
        setShowReceipt(true);
        localStorage.setItem("lastReceipt", JSON.stringify(receipt));
      } else {
        alert("Ошибка: " + (data.message || "Неизвестная ошибка"));
        const saved = localStorage.getItem("lastReceipt");
        if (saved) {
          setReceiptData(JSON.parse(saved));
          setShowReceipt(true);
        }
      }
    } catch (err) {
      console.error("Ошибка при отправке:", err);
      alert("Ошибка при подключении к API");
      const saved = localStorage.getItem("lastReceipt");
      if (saved) {
        setReceiptData(JSON.parse(saved));
        setShowReceipt(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* LEFT */}
        <div className={styles.left}>
          <div className={styles.quickBtns}>
            {[1000, 2000, 5000, 10000].map((val) => (
              <button key={val} onClick={() => setPaymentAmountByInput(String(val))}>
                {val}
              </button>
            ))}
          </div>

          <div className={styles.numPad}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, ".", 0].map((n) => (
              <button key={n} onClick={() => handleKeyClick(String(n))}>
                {n}
              </button>
            ))}
            <button onClick={handleDelete}>⌫</button>
          </div>
        </div>

        {/* RIGHT */}
        <div className={styles.right}>
          <div className={styles.tabs}>
            <div className={styles.tabWithMenu}>
              <button
                className={activeTab === "cash" ? styles.active : ""}
                onClick={() => applyTypeToActivePayment("cash")}
              >
                Наличные
              </button>
            </div>

            <div className={styles.tabWithMenu}>
              <button
                className={activeTab === "card" ? styles.active : ""}
                onClick={() => applyTypeToActivePayment("card")}
              >
                Картой
              </button>

              <span className={styles.dots} onClick={() => setShowWalletMenu((p) => !p)}>
                ⋮
              </span>

              {showWalletMenu && (
                <div className={styles.walletMenu}>
                  <div
                    className={styles.walletItem}
                    onClick={() => handleSelectWallet(DEBT_WALLET)}
                  >
                    {DEBT_WALLET.title}
                  </div>

                  {wallets.map((w) => (
                    <div
                      key={w.id}
                      className={styles.walletItem}
                      onClick={() => handleSelectWallet(w)}
                    >
                      {w.title}
                    </div>
                  ))}
                </div>
              )}

              <ClientSelectModal
                open={showClientSelect}
                onClose={() => setShowClientSelect(false)}
                onSelectClient={(client) => {
                  setSelectedClient(client);
                  setShowClientSelect(false);
                }}
              />
            </div>
          </div>

          <div className={styles.payments}>
            {payments.map((p, i) => {
              const label = p.type === "cash" ? "Наличные" : p.title || "Карта";

              return (
                <div
                  key={i}
                  className={`${styles.paymentRow} ${
                    i === activePaymentIndex ? styles.activeRow : ""
                  }`}
                  onClick={() => setActivePaymentIndex(i)}
                >
                  <span>{label}</span>

                  <input
                    value={
                      i === activePaymentIndex ? amountInput : p.amountStr ?? String(p.amount ?? 0)
                    }
                    onChange={(e) => {
                      if (i !== activePaymentIndex) setActivePaymentIndex(i);
                      const v = e.target.value.replace(",", ".");
                      if (!/^\d*\.?\d*$/.test(v)) return;
                      setPaymentAmountByInput(v);
                    }}
                  />

                  {payments.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removePayment(i);
                      }}
                    >
                      ❌
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className={styles.actions}>
            <button onClick={addPayment}>Добавить оплату</button>

            <button type="button" onClick={() => setShowDiscount((prev) => !prev)}>
              Скидка на чек
            </button>
          </div>

          {showDiscount && (
            <div className={styles.discountWrap}>
              <div className={styles.discountLabel}>Скидка на весь чек</div>

              <div className={styles.discountRow}>
                <input
                  className={styles.discountInput}
                  value={discountInput}
                  onChange={(e) => {
                    const v = e.target.value.replace(",", ".");
                    if (!/^\d*\.?\d*$/.test(v)) return;
                    setDiscountInput(v);
                  }}
                  placeholder="0"
                />

                <button
                  type="button"
                  className={`${styles.discountBtn} ${
                    discountType === "percent" ? styles.discountBtnActive : ""
                  }`}
                  onClick={() => setDiscountType("percent")}
                  title="Процент"
                >
                  %
                </button>

                <button
                  type="button"
                  className={`${styles.discountBtn} ${
                    discountType === "amount" ? styles.discountBtnActive : ""
                  }`}
                  onClick={() => setDiscountType("amount")}
                  title="Сумма"
                >
                  ₸
                </button>

                <button
                  type="button"
                  className={styles.discountClose}
                  onClick={() => {
                    setShowDiscount(false);
                    setDiscountInput("0");
                    setDiscountType("percent");
                  }}
                  title="Закрыть"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          <textarea placeholder="Введите комментарий" />

          <div className={styles.summary}>
            <p>
              Итого: <strong>{baseTotal} ₸</strong>
            </p>

            {discountAmount > 0 && (
              <p>
                Скидка: <strong>-{Math.round(discountAmount)} ₸</strong>
              </p>
            )}

            <p>
              К оплате: <strong>{Math.round(finalTotal)} ₸</strong>
            </p>

            <p>
              Оплачено: <strong>{totalPaid} ₸</strong>
            </p>

            <p>
              Сдача: <strong>{change > 0 ? change : 0} ₸</strong>
            </p>
          </div>

          <button
            className={styles.confirm}
            onClick={handleSubmit}
            disabled={loading || !products || products.length === 0}
          >
            {loading ? "Отправка..." : "Выбить чек"}
          </button>

          <button className={styles.close} onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>

      <ReceiptModal open={showReceipt} onClose={() => setShowReceipt(false)} receipt={receiptData} />
    </div>
  );
};

export default PaymentModal;
